import { useMemo, useState, type ReactNode } from 'react'
import { fbt } from 'fbtee'
import { Check, Globe, MapPinPlus, Search } from 'lucide-react'
import { REGIONS, EVERYWHERE_REGION_ID } from '@shared/regions'
import { CountBadge } from './CountBadge'
import { Input } from './ui/input'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { RequestRegionDialog } from './RequestRegionDialog'
import { useAuth } from '../hooks/useAuth'
import { useGuestGuard } from '../hooks/useGuestGuard'
import { useRegionRequests } from '../hooks/useRegionRequests'
import { useRegionEventCounts } from '../hooks/useRegionEventCounts'
import { requestRegionPrompt } from '../lib/guestPrompts'
import { cn } from '../lib/utils'

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

function Row({
  label,
  icon,
  count,
  selected,
  onClick,
  testId,
}: {
  label: ReactNode
  icon?: ReactNode
  count?: number
  selected: boolean
  onClick: () => void
  testId: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-left',
        'cursor-pointer transition-colors hover:bg-muted/60',
        selected && 'bg-muted/40',
      )}
    >
      {icon && <span className="shrink-0 text-muted-foreground">{icon}</span>}
      <span className="flex-1 min-w-0 truncate">{label}</span>
      {count !== undefined && <CountBadge count={count} data-testid={`${testId}-count`} />}
      {selected && <Check className="size-4 shrink-0 text-primary" />}
    </button>
  )
}

interface Props {
  regionId: string
  onSelect: (regionId: string) => void
  /** The control the list hangs off — the location chip, or a settings row. */
  children: ReactNode
  align?: 'start' | 'center' | 'end'
}

/**
 * Region list, anchored to whatever opened it. A popover rather than a dialog:
 * picking a place is a small, reversible adjustment to what you're already
 * looking at, and a modal makes it feel like a decision the app is blocking on.
 *
 * Owns the request-an-area flow itself rather than taking it as a prop: it is
 * the same offer wherever the picker appears, and the dialog is a sibling of
 * the popover so closing the popover doesn't unmount it mid-flow.
 *
 * Each region shows its upcoming event count, so a bare region is honest up
 * front. Counted when the list opens, not with the page.
 */
export function RegionPicker({ regionId, onSelect, children, align = 'start' }: Props) {
  const [open, setOpen] = useState(false)
  const eventCounts = useRegionEventCounts(open)
  const [search, setSearch] = useState('')
  const [requesting, setRequesting] = useState(false)
  const { signedInUid } = useAuth()
  const { guard } = useGuestGuard()
  const { requestRegion } = useRegionRequests(signedInUid)
  const openRequest = guard(() => setRequesting(true), requestRegionPrompt())

  const matches = useMemo(() => {
    const q = normalize(search)
    const sorted = [...REGIONS].sort((a, b) => a.name.localeCompare(b.name))
    if (!q) return sorted
    return sorted.filter(
      r => normalize(r.name).includes(q) || r.cities.some(c => normalize(c).includes(q)),
    )
  }, [search])

  function choose(id: string) {
    onSelect(id)
    setOpen(false)
    setSearch('')
  }

  return (
    <>
      {/* `modal` so an outside tap only dismisses the list, like the dropdown
          menus, instead of also landing on whatever is underneath. */}
      <Popover
        modal
        open={open}
        onOpenChange={next => {
          setOpen(next)
          if (!next) setSearch('')
        }}
      >
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          data-modal=""
          align={align}
          className="w-[min(20rem,calc(100vw-2rem))] p-2"
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <div className="relative mb-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={fbt(
                'Search areas',
                'Placeholder for the region picker search box',
              ).toString()}
              className="pl-8 h-8"
            />
          </div>

          <div className="max-h-72 overflow-y-auto">
            <Row
              testId="region-option-everywhere"
              icon={<Globe className="size-4" />}
              label={
                <fbt desc="Region picker option that shows events from every region without grouping">
                  Everywhere
                </fbt>
              }
              selected={regionId === EVERYWHERE_REGION_ID}
              onClick={() => choose(EVERYWHERE_REGION_ID)}
            />
            {matches.map(region => (
              <Row
                key={region.id}
                testId={`region-option-${region.id}`}
                label={region.name}
                count={eventCounts ? (eventCounts.get(region.id) ?? 0) : undefined}
                selected={regionId === region.id}
                onClick={() => choose(region.id)}
              />
            ))}
          </div>

          {/* Below the list, and shown to guests too — the guard handles the
              upsell, and hiding it would leave them no way to say they exist. */}
          <div className="mt-1 border-t border-border pt-1">
            <Row
              testId="region-request-open"
              icon={<MapPinPlus className="size-4" />}
              label={
                <fbt desc="Row under the region list that opens the request-an-area form">
                  Don't see your area?
                </fbt>
              }
              selected={false}
              onClick={() => {
                setOpen(false)
                setSearch('')
                openRequest()
              }}
            />
          </div>
        </PopoverContent>
      </Popover>

      <RequestRegionDialog
        open={requesting}
        onOpenChange={setRequesting}
        source="region_picker"
        onSubmit={requestRegion}
        onPickCovered={onSelect}
      />
    </>
  )
}
